import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { fetchUserData } from '../services/publicDataService';
import screamifyLogo from '../img/screamifyweblogo.png';
import profileDataService from '../services/profileDataService';
import {
  Box,
  Typography,
  Paper,
  Link,
  Container,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Avatar,
  Grid,
  Divider,
  IconButton,
  Button,
  Collapse,
  Pagination,
  Menu,
  MenuItem
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ForumIcon from '@mui/icons-material/Forum';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LaunchIcon from '@mui/icons-material/Launch';
import SortIcon from '@mui/icons-material/Sort';
import { keyframes } from '@emotion/react';

// Define some animations
const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(255, 0, 0, 0.5), 0 0 10px rgba(255, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(255, 0, 0, 0.7), 0 0 20px rgba(255, 0, 0, 0.5);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 0, 0, 0.5), 0 0 10px rgba(255, 0, 0, 0.3);
  }
`;

const bloodDrip = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 0 100px;
  }
`;

const HorrorTree = () => {
  // Extract the 'userhandle' param from the route
  const { userhandle } = useParams();
  // Remove "@" if it exists and convert to lowercase for case-insensitive matching
  const cleanHandle = userhandle && userhandle.startsWith('@') ? userhandle.slice(1).toLowerCase() : userhandle?.toLowerCase();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const reviewsPerPage = 4;
  // Add sort state and menu anchor
  const [sortMethod, setSortMethod] = useState('new'); // 'top' or 'new'
  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);

  // Add state for profile images
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [bannerImageUrl, setBannerImageUrl] = useState(null);

  const [profileImageError, setProfileImageError] = useState(false);
  const [bannerImageError, setBannerImageError] = useState(false);

  // Menu handlers for sorting
  const handleSortMenuOpen = (event) => {
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortChange = (method) => {
    setSortMethod(method);
    handleSortMenuClose();
    // Reset to first page when changing sort
    setPage(1);
  };
  
  // Handle page change
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    // Scroll to top of reviews section if it exists
    const reviewsSection = document.getElementById('reviews-section');
    if (reviewsSection) {
      window.scrollTo({
        top: reviewsSection.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (cleanHandle) {
      setLoading(true);
      console.log(`Fetching data for handle: ${cleanHandle}`);
      fetchUserData(cleanHandle)
        .then(data => {
          console.log('User data fetched:', data);
          setUserData(data);
          
          // For profile images, use the clean handle directly
          if (cleanHandle) {
            console.log(`Setting profile images for handle: ${cleanHandle}`);
            
            // Set the URLs using the user handle
            const avatarUrl = profileDataService.getProfileAssetUrl('avatar', cleanHandle);
            const bannerUrl = profileDataService.getProfileAssetUrl('banner', cleanHandle);
            
            console.log('Avatar URL:', avatarUrl);
            console.log('Banner URL:', bannerUrl);
            
            // Set the URLs directly without checking if they exist
            // We'll handle image load errors with onError handlers
            setProfileImageUrl(avatarUrl);
            setBannerImageUrl(bannerUrl);
            
            // Reset error states
            setProfileImageError(false);
            setBannerImageError(false);
          }
          
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching data:', err);
          setError(err.message);
          setLoading(false);
        });
    }
  }, [cleanHandle]);

  // Define memoized values that we'll use later
  const reviewsAndComments = useMemo(() => {
    if (!userData || !userData.reviews) return { mainReviews: [], reviewsWithCommentCounts: [] };
    
    // Filter to show only threads (main reviews) and not posts (comments)
    const mainReviews = userData.reviews.filter(review => review.type === "thread");
    
    // Calculate comment counts for each thread
    const commentCounts = {};
    userData.reviews.forEach(item => {
      if (item.type === "post") {
        commentCounts[item.threadId] = (commentCounts[item.threadId] || 0) + 1;
      }
    });
    
    // Add comment counts to main reviews
    const reviewsWithCommentCounts = mainReviews.map(review => ({
      ...review,
      commentCount: commentCounts[review.threadId] || 0
    }));
    
    return { mainReviews, reviewsWithCommentCounts };
  }, [userData]);
  
  // Sort reviews based on selected method
  const sortedReviews = useMemo(() => {
    const { reviewsWithCommentCounts } = reviewsAndComments;
    if (!reviewsWithCommentCounts || reviewsWithCommentCounts.length === 0) {
      return [];
    }
    
    return [...reviewsWithCommentCounts].sort((a, b) => {
      if (sortMethod === 'top') {
        const aVotes = (a.upvotes || 0) - (a.downvotes || 0);
        const bVotes = (b.upvotes || 0) - (b.downvotes || 0);
        return bVotes - aVotes;
      } else {
        // Sort by most recent (default)
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  }, [reviewsAndComments, sortMethod]);
  
  // Calculate pagination
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(sortedReviews.length / reviewsPerPage);
    const startIndex = (page - 1) * reviewsPerPage;
    const paginatedReviews = sortedReviews.slice(startIndex, startIndex + reviewsPerPage);
    
    return { totalPages, paginatedReviews };
  }, [sortedReviews, page, reviewsPerPage]);

  // Handle error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#0d0d0d',
          background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0000 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          p: 3
        }}
      >
        <Typography variant="h4" sx={{ color: '#ff0000' }}>
          Error: {error}
        </Typography>
      </Box>
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#0d0d0d',
          background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0000 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress sx={{ color: '#ff0000' }} size={60} />
      </Box>
    );
  }

  const { totalPages, paginatedReviews } = paginationData;
  const social = userData?.social || {};

  // Log state variables when rendering
  console.log('Rendering HorrorTree component with state:');
  console.log('- cleanHandle:', cleanHandle);
  console.log('- profileImageUrl:', profileImageUrl);
  console.log('- bannerImageUrl:', bannerImageUrl);
  console.log('- profileImageError:', profileImageError);
  console.log('- bannerImageError:', bannerImageError);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0a0a0a',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a0000 100%)',
        color: '#fff',
        py: 4,
        overflowX: 'hidden'
      }}
    >
      <Container maxWidth="sm">
        {/* Horror Tree Title */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4,
          animation: `${pulseGlow} 3s infinite`
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              letterSpacing: 1,
              textTransform: 'uppercase',
              background: 'linear-gradient(to right, #ff0000, #ff6b6b)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
              mb: 1,
              fontSize: { xs: '2rem', sm: '2.5rem' }
            }}
          >
            HORROR TREE
          </Typography>
        </Box>

        {/* Banner Image - with improved error handling */}
        {bannerImageUrl ? (
          <Box 
            sx={{
              height: '300px',
              width: '100%',
              mb: 4,
              position: 'relative',
              borderRadius: 3,
              border: '1px solid rgba(255, 0, 0, 0.2)',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
              overflow: 'hidden'
            }}
          >
            <Box
              component="img"
              src={bannerImageUrl}
              alt="Profile Banner"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: bannerImageError ? 'none' : 'block'
              }}
              onError={(e) => {
                console.log('Banner image error event triggered');
                setBannerImageError(true);
                // Hide the image on error
                e.target.style.display = 'none';
              }}
            />
            {bannerImageError && (
              <Box sx={{ 
                width: '100%', 
                height: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                borderRadius: 3
              }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontStyle: 'italic' }}>
                  Banner not available
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}

        {/* Profile Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4
          }}
        >
          {/* Profile Image - with more detailed checks and logging */}
          <Avatar
            src={profileImageUrl && !profileImageError ? profileImageUrl : ''}
            sx={{
              width: 96,
              height: 96,
              mb: 2,
              bgcolor: 'rgba(255, 0, 0, 0.2)',
              border: '4px solid rgba(255, 0, 0, 0.3)',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)',
              // If banner exists, pull the avatar up into the banner
              mt: bannerImageUrl && !bannerImageError ? '-80px' : 0
            }}
            imgProps={{
              onError: (e) => {
                console.log('Avatar image error event triggered');
                setProfileImageError(true);
                e.target.src = screamifyLogo;
                // Adjust styles for logo
                e.target.style.width = '60%';
                e.target.style.height = 'auto';
                e.target.style.margin = 'auto';
              }
            }}
          >
            {cleanHandle ? cleanHandle[0].toUpperCase() : 'U'}
          </Avatar>

          {/* Username */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: '#fff',
              textShadow: '0 0 10px rgba(255, 0, 0, 0.3)'
            }}
          >
            @{cleanHandle}
          </Typography>

          {/* Bio */}
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              mb: 3,
              maxWidth: '80%',
              whiteSpace: 'pre-line'
            }}
          >
            {social.userBio || 'Discover my horror reviews and where you can find me 🔪'}
          </Typography>

          {/* Social Links Section */}
          <Paper
            elevation={3}
            sx={{
              bgcolor: 'rgba(20, 20, 20, 0.85)',
              borderRadius: 3,
              p: { xs: 3, sm: 4 },
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 0, 0, 0.2)',
              mb: 4,
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)'
            }}
          >
            <Grid container spacing={2}>
              {social.userInstagram && (
                <Grid item xs={12}>
                  <Link href={`https://instagram.com/${social.userInstagram.replace(/^@+/, '')}`} target="_blank" underline="none">
                    <Paper
                      sx={{
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(40,10,10,0.8) 100%)',
                        p: 0,
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        position: 'relative',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, transparent, #E4405F, transparent)',
                          opacity: 0.7,
                        },
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          animation: `${pulseGlow} 2s infinite`,
                          '& .insta-icon': {
                            transform: 'scale(1.2) rotate(15deg)',
                            filter: 'drop-shadow(0 0 8px rgba(228, 64, 95, 0.8))'
                          },
                          '& .insta-gradient': {
                            opacity: 0.9,
                            background: 'linear-gradient(135deg, rgba(64, 93, 230, 0.4) 0%, rgba(225, 48, 108, 0.4) 50%, rgba(252, 175, 69, 0.4) 100%)',
                          }
                        }
                      }}
                    >
                      <Box className="insta-gradient" sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.3,
                        background: 'linear-gradient(135deg, rgba(64, 93, 230, 0.2) 0%, rgba(225, 48, 108, 0.2) 50%, rgba(252, 175, 69, 0.2) 100%)',
                        transition: 'all 0.5s ease',
                        zIndex: 1
                      }} />
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        position: 'relative', 
                        zIndex: 2,
                        p: 2
                      }}>
                        <Box sx={{ 
                          backgroundColor: 'rgba(0,0,0,0.3)', 
                          borderRadius: '50%', 
                          p: 1.5,
                          display: 'flex',
                          mr: 2
                        }}>
                          <InstagramIcon 
                            className="insta-icon"
                            sx={{ 
                              color: '#E4405F', 
                              fontSize: 36, 
                              transition: 'all 0.3s ease',
                              filter: 'drop-shadow(0 0 5px rgba(228, 64, 95, 0.5))'
                            }} 
                          />
                        </Box>
                        <Typography variant="h6" sx={{ 
                          color: '#fff',
                          fontWeight: 'bold',
                          textShadow: '0 0 10px rgba(255, 0, 0, 0.5)'
                        }}>
                          Instagram
                        </Typography>
                        <Box sx={{ 
                          ml: 'auto',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            transform: 'scale(1.1)',
                          }
                        }}>
                          <Box sx={{ 
                            borderTop: '2px solid rgba(255,255,255,0.8)',
                            borderRight: '2px solid rgba(255,255,255,0.8)',
                            width: '8px',
                            height: '8px',
                            transform: 'rotate(45deg)',
                            marginLeft: '-3px'
                          }} />
                        </Box>
                      </Box>
                    </Paper>
                  </Link>
                </Grid>
              )}
              
              {social.userTikTok && (
                <Grid item xs={12}>
                  <Link href={`https://tiktok.com/@${social.userTikTok.replace(/^@+/, '')}`} target="_blank" underline="none">
                    <Paper
                      sx={{
                        background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(10,20,30,0.8) 100%)',
                        p: 0,
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        position: 'relative',
                        '&:before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          width: '4px',
                          background: `linear-gradient(to bottom, #00f2ea, #ff0050)`,
                          opacity: 0.7,
                        },
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          animation: `${pulseGlow} 2s infinite`,
                          '& .tiktok-icon': {
                            transform: 'scale(1.2) rotate(-15deg)',
                            filter: 'drop-shadow(0 0 8px rgba(0, 242, 234, 0.8))'
                          },
                          '& .tiktok-gradient': {
                            opacity: 0.9,
                          },
                          '& .tiktok-logo': {
                            filter: 'brightness(1.3) drop-shadow(0 0 10px rgba(255, 0, 80, 0.8))'
                          }
                        }
                      }}
                    >
                      <Box className="tiktok-gradient" sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.3,
                        background: 'radial-gradient(circle at top right, rgba(0, 242, 234, 0.3), rgba(0, 0, 0, 0) 50%), radial-gradient(circle at bottom left, rgba(255, 0, 80, 0.3), rgba(0, 0, 0, 0) 50%)',
                        transition: 'all 0.5s ease',
                        zIndex: 1
                      }} />
                      
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        position: 'relative', 
                        zIndex: 2,
                        p: 2
                      }}>
                        {/* TikTok Logo Container */}
                        <Box 
                          sx={{ 
                            backgroundColor: 'black', 
                            borderRadius: '8px', 
                            p: 0.8,
                            width: '40px',
                            height: '40px',
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 2,
                            overflow: 'hidden'
                          }}
                        >
                          {/* Red blob */}
                          <Box 
                            sx={{
                              position: 'absolute',
                              top: '3px',
                              right: '3px',
                              width: '16px',
                              height: '16px',
                              background: '#ff004f',
                              borderRadius: '50%',
                              filter: 'blur(2px)',
                            }}
                          />
                          
                          {/* Cyan blob */}
                          <Box 
                            sx={{
                              position: 'absolute',
                              bottom: '3px',
                              left: '3px',
                              width: '16px',
                              height: '16px',
                              background: '#00f2ea',
                              borderRadius: '50%',
                              filter: 'blur(2px)',
                            }}
                          />
                          
                          {/* White TikTok logo */}
                          <Box 
                            className="tiktok-icon"
                            sx={{
                              width: '22px',
                              height: '25px',
                              background: 'white',
                              clipPath: 'polygon(0% 0%, 60% 0%, 60% 100%, 0% 100%)',
                              position: 'relative',
                              zIndex: 5,
                              transition: 'all 0.3s ease',
                            }}
                          >
                            {/* TikTok musical note */}
                            <MusicNoteIcon
                              sx={{
                                position: 'absolute',
                                color: 'black',
                                fontSize: '26px',
                                top: '-2px',
                                left: '-2px',
                                zIndex: 6,
                                transform: 'rotate(5deg)'
                              }}
                            />
                          </Box>
                        </Box>
                        
                        <Typography variant="h6" sx={{ 
                          color: '#fff',
                          fontWeight: 'bold', 
                          textShadow: '0 0 10px rgba(255, 0, 0, 0.5)'
                        }}>
                          TikTok
                        </Typography>
                        
                        <Box sx={{ 
                          ml: 'auto',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            transform: 'scale(1.1)',
                          }
                        }}>
                          <Box sx={{ 
                            borderTop: '2px solid rgba(255,255,255,0.8)',
                            borderRight: '2px solid rgba(255,255,255,0.8)',
                            width: '8px',
                            height: '8px',
                            transform: 'rotate(45deg)',
                            marginLeft: '-3px'
                          }} />
                        </Box>
                      </Box>
                    </Paper>
                  </Link>
                </Grid>
              )}
              
              {social.userX && (
                <Grid item xs={12}>
                  <Link href={`https://x.com/${social.userX.replace(/^@+/, '')}`} target="_blank" underline="none">
                    <Paper
                      sx={{
                        bgcolor: 'rgba(29, 161, 242, 0.2)',
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
                          bgcolor: 'rgba(29, 161, 242, 0.3)'
                        }
                      }}
                    >
                      <TwitterIcon sx={{ color: '#1DA1F2', fontSize: 30, mr: 2 }} />
                      <Typography variant="h6" sx={{ color: '#fff' }}>X</Typography>
                      <Box sx={{ 
                        ml: 'auto',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          transform: 'scale(1.1)',
                        }
                      }}>
                        <Box sx={{ 
                          borderTop: '2px solid rgba(255,255,255,0.8)',
                          borderRight: '2px solid rgba(255,255,255,0.8)',
                          width: '8px',
                          height: '8px',
                          transform: 'rotate(45deg)',
                          marginLeft: '-3px'
                        }} />
                      </Box>
                    </Paper>
                  </Link>
                </Grid>
              )}
              
              {social.userYouTube && (
                <Grid item xs={12}>
                  <Link href={`https://youtube.com/${social.userYouTube}`} target="_blank" underline="none">
                    <Paper
                      sx={{
                        bgcolor: 'rgba(255, 0, 0, 0.2)',
                        p: 2,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.3s ease',
                        border: '1px solid rgba(255, 0, 0, 0.3)',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 5px 15px rgba(255, 0, 0, 0.3)',
                          bgcolor: 'rgba(255, 0, 0, 0.3)'
                        }
                      }}
                    >
                      <YouTubeIcon sx={{ color: '#FF0000', fontSize: 30, mr: 2 }} />
                      <Typography variant="h6" sx={{ color: '#fff' }}>YouTube</Typography>
                      <Box sx={{ 
                        ml: 'auto',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          transform: 'scale(1.1)',
                        }
                      }}>
                        <Box sx={{ 
                          borderTop: '2px solid rgba(255,255,255,0.8)',
                          borderRight: '2px solid rgba(255,255,255,0.8)',
                          width: '8px',
                          height: '8px',
                          transform: 'rotate(45deg)',
                          marginLeft: '-3px'
                        }} />
                      </Box>
                    </Paper>
                  </Link>
                </Grid>
              )}
              
              {!social.userInstagram && !social.userTikTok && !social.userX && !social.userYouTube && (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      bgcolor: 'rgba(50, 50, 50, 0.3)',
                      p: 3,
                      borderRadius: 2,
                      textAlign: 'center',
                      border: '1px dashed rgba(255, 0, 0, 0.3)',
                    }}
                  >
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      No social media links available.
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Reviews Section */}
          <Paper
            id="reviews-section"
            elevation={3}
            sx={{
              bgcolor: 'rgba(20, 20, 20, 0.85)',
              borderRadius: 3,
              p: { xs: 3, sm: 4 },
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255, 0, 0, 0.2)',
              boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 3,
              flexWrap: 'wrap'
            }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                color: '#ff0000',
                flex: { xs: '1 0 100%', sm: '0 1 auto' },
                textAlign: { xs: 'center', sm: 'left' },
                mb: { xs: 2, sm: 0 }
              }}>
                MY HORROR REVIEWS
              </Typography>
              
              {sortedReviews.length > 0 && (
                <Button 
                  variant="contained"
                  endIcon={<SortIcon />}
                  onClick={handleSortMenuOpen}
                  sx={{
                    bgcolor: '#ff0000',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#cc0000',
                    },
                    ml: { xs: 'auto', sm: 0 },
                    mr: { xs: 'auto', sm: 0 }
                  }}
                >
                  Sort: {sortMethod === 'top' ? 'Top Rated' : 'Most Recent'}
                </Button>
              )}
              
              <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleSortMenuClose}
                PaperProps={{
                  sx: {
                    bgcolor: 'rgba(30, 30, 30, 0.95)',
                    border: '1px solid rgba(255, 0, 0, 0.3)',
                    color: 'white'
                  }
                }}
              >
                <MenuItem 
                  onClick={() => handleSortChange('top')}
                  sx={{ 
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' } 
                  }}
                >
                  Top Rated
                </MenuItem>
                <MenuItem 
                  onClick={() => handleSortChange('new')}
                  sx={{ 
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.1)' } 
                  }}
                >
                  Most Recent
                </MenuItem>
              </Menu>
            </Box>

            {sortedReviews.length > 0 ? (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: 3 
                }}>
                  {paginatedReviews.map((review, index) => (
                    <ExpandableReviewCard 
                      key={index}
                      review={review}
                    />
                  ))}
                </Box>
                
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination 
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      variant="outlined"
                      shape="rounded"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          borderColor: 'rgba(255, 0, 0, 0.3)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.1)'
                          }
                        },
                        '& .Mui-selected': {
                          backgroundColor: 'rgba(255, 0, 0, 0.2) !important',
                          color: '#fff',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                borderRadius: 2,
                border: '1px dashed rgba(255, 255, 255, 0.1)'
              }}>
                <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>No reviews yet</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
                  This user hasn't posted any horror reviews.
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Footer with branding */}
          <Box sx={{ textAlign: 'center', mt: 5, mb: 2, opacity: 0.7 }}>
            <img 
              src={screamifyLogo} 
              alt="Screamify Logo" 
              style={{ width: '60px', marginBottom: '10px' }} 
            />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Powered by Screamify™
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

// Expandable Review Card Component
const ExpandableReviewCard = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  
  const handleExpandClick = () => {
    setExpanded(!expanded);
  };
  
  // Format date to a readable format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    };
    
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  return (
    <Card 
      sx={{ 
        bgcolor: 'rgba(0, 0, 0, 0.7)', 
        color: 'white',
        border: '1px solid rgba(255, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 5px 15px rgba(255, 0, 0, 0.2)',
          borderColor: 'rgba(255, 0, 0, 0.5)',
        },
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Collapsed View */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
        {review.threadPoster && (
          <Box 
            sx={{ 
              display: 'block',
              width: { xs: '100%', sm: '140px' },
              height: { xs: '180px', sm: '200px' },
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0,
              margin: { xs: 0, sm: '16px' },
              borderBottom: { xs: '1px solid rgba(255, 0, 0, 0.2)', sm: 'none' },
              cursor: 'pointer'
            }}
            onClick={handleExpandClick}
          >
            <Box
              component="img"
              src={review.threadPoster}
              alt={review.title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'all 0.5s ease'
              }}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title={
              <Typography 
                variant="h6" 
                sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                onClick={handleExpandClick}
              >
                {review.title}
              </Typography>
            }
            subheader={
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {formatDate(review.createdAt)}
                </Typography>
              </Box>
            }
            sx={{ pt: 2, pb: 0 }}
          />
          <CardContent sx={{ pt: 1 }} onClick={handleExpandClick}>
            <Typography variant="body2" sx={{
              color: 'rgba(255,255,255,0.7)',
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: expanded ? 'unset' : 2,
              whiteSpace: 'pre-line',
              cursor: 'pointer'
            }}>
              {!expanded && review.content.length > 120 
                ? `${review.content.substring(0, 120)}...` 
                : !expanded ? review.content : null}
            </Typography>
          </CardContent>
          
          {/* Stats Row (always visible) */}
          <Box sx={{ 
            px: 2, 
            pb: 2, 
            pt: 0, 
            display: 'flex', 
            alignItems: 'center',
          }}>
            {/* Vote and Comment Counts */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ThumbUpIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)', mr: 0.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mr: 1.5 }}>
                {review.upvotes || 0}
              </Typography>
              <ThumbDownIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)', mr: 0.5 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mr: 2 }}>
                {review.downvotes || 0}
              </Typography>
              {/* Comment Count */}
              {review.commentCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <ForumIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)', mr: 0.5 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    {review.commentCount}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      
      {/* Expand/Collapse Button */}
      <Button 
        onClick={handleExpandClick}
        size="small"
        startIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        sx={{ 
          position: 'absolute', 
          bottom: 8, 
          right: 8, 
          color: 'rgba(255,0,0,0.7)',
          backgroundColor: 'rgba(0,0,0,0.3)',
          '&:hover': {
            backgroundColor: 'rgba(255,0,0,0.1)',
          },
          transition: 'all 0.2s ease',
          textTransform: 'none'
        }}
      >
        {expanded ? 'Collapse' : 'Expand'}
      </Button>
      
      {/* Expanded Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: 'rgba(255, 0, 0, 0.2)' }} />
        <CardContent sx={{ pb: 6 }}>
          <Typography 
            variant="body1" 
            sx={{
              color: 'rgba(255,255,255,0.9)',
              whiteSpace: 'pre-line',
              lineHeight: 1.8
            }}
          >
            {review.content}
          </Typography>
          <Box sx={{ mt: 3, mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<LaunchIcon />}
              component={Link}
              href={`https://screamify.com/reviews/${review.threadId}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderColor: 'rgba(255, 0, 0, 0.5)',
                color: '#ff0000',
                '&:hover': {
                  backgroundColor: 'rgba(255, 0, 0, 0.1)',
                  borderColor: '#ff0000'
                }
              }}
            >
              Comment on Screamify.com
            </Button>
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default HorrorTree; 